/**
 * Bounds for the owner-entered purchase-planning fields, in one place.
 *
 * The API zod schemas, the web input parsers, and the database CHECK
 * constraints all read these, so a value the API accepts can never be one the
 * database rejects. The database enforces the full min AND max on the per-SKU
 * planning integers and unit cost (migration 0014, over the lower-bound-only
 * 0011) and the lead/cover/safety ranges on the settings row (0012); a test
 * asserts the migration SQL matches these numbers. See docs/analysis.md.
 */

export interface IntBound {
  min: number;
  max: number;
}

export const PLANNING_BOUNDS = {
  /** Lead time: both the global setting and the per-SKU override. */
  leadTimeDays: { min: 0, max: 365 },
  coverDays: { min: 1, max: 365 },
  safetyPct: { min: 0, max: 200 },
  casePack: { min: 1, max: 1_000_000 },
  minOrderQty: { min: 1, max: 10_000_000 },
  /** A plan's quantity override (`purchase_plan_override.qty`). */
  overrideQty: { min: 0, max: 10_000_000 },
} as const satisfies Record<string, IntBound>;

/** Unit cost is a non-negative `numeric(12,2)`: up to ten integer digits, two decimals. */
export const UNIT_COST_DECIMALS = 2;
export const UNIT_COST_MAX_INTEGER_DIGITS = 10;

/** The velocity floor used when a SKU has few in-stock days (documented, not a setting). */
export const MIN_IN_STOCK_DAYS_FOR_VELOCITY = 14;

/**
 * How the plan sizes demand. `recent` is the rolling plan: the recent rate
 * over lead time + cover. `seasonal` is a dated order: last year's rate over
 * the same calendar window, from today through the horizon date, scaled by
 * this year's trend. See docs/analysis.md, "Seasonal demand".
 */
export const DEMAND_BASES = ['recent', 'seasonal'] as const;
export type DemandBasis = (typeof DEMAND_BASES)[number];

/** A seasonal horizon may be at most this far ahead of today. */
export const HORIZON_MAX_DAYS = 365;

/**
 * The per-SKU trend factor (this year's last 90 days against last year's
 * same 90 days) is clamped to this range, so one strong or weak quarter can
 * at most halve or double last year's seasonal rate.
 */
export const TREND_CLAMP = { min: 0.5, max: 2 } as const;

/** The budget is a non-negative `numeric(12,2)`, like unit cost. */
export const BUDGET_MAX = '9999999999.99';
